import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faClockRotateLeft } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule, PageHeader,

} from '@domain-group/suit-theme'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useAgencyActivities from '@hooks/useAgencyActivities'

import defaultRequestOptions from '@sections/Client/packs/ActivityLogs/defaultRequestOptions'

import ActivityItem from '@components/ActivityListItem'
import PageFilterModule from '@components/PageFilterModule'

import ActivityBlankState from '../BlankState'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_date_start',
      label: 'Activity After',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        inputType: 'date',
        style,
      },
      key: 'created_date_end',
      label: 'Activity Before',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        entityKey: 'agencies',
        style,
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        entityKey: 'campaigns',
        style,
      },
      key: 'campaign_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const activitiesPayload = useAgencyActivities({
    // @ts-expect-error TS2322
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.activity,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading,
  } = activitiesPayload

  return (
    <>
      <Helmet>
        <title>Activity logs | Realhub</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="activities"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: '#/activity-logs',
            icon: faClockRotateLeft,
            key: 'activities',
            title: 'Activities',
          },
        ]}
        title="Activities"
        offset={{ left: '220px' }}
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredActivities && <ActivityBlankState />}

            {!loading && hasFilteredActivities && (
              <Box flexDirection="column">
                <DashboardModule
                  contentBoxProps={{ flexDirection: 'column' }}
                  title="Activities"
                >
                  <DashboardModule.ScrollableSection
                    // @ts-expect-error TS2322
                    alignItems={hasFilteredActivities ? undefined : 'center'}
                    flexDirection="column"
                    justifyContent={hasFilteredActivities ? undefined : 'center'}
                    marginTop={0}
                    maxHeight={800}
                  >
                    {(filteredActivities.map((activity, index) => (
                      <ActivityItem
                        activity={activity}
                        boxProps={{ paddingX: 'large', paddingY: 'large' }}
                        isLastActivity={index === (filteredActivitiesCount - 1)}
                        key={activity.id}
                      />
                    )))}
                  </DashboardModule.ScrollableSection>
                </DashboardModule>
              </Box>
            )}

            <Box variant="white">
              <DashboardModule.LoadMoreFooter
                callbacks={{ loadMore }}
                canLoadMore={canLoadMore}
                entityCount={filteredActivitiesCount}
                loading={loading}
                showBorderTop={false}
              />
            </Box>
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
